import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { client } from "../../Clients";
import type { BCCADMINBaseModelsContentTypeFilterItem } from "../../API/Models";
import { isHiddenBrowseType } from "../../Pages/Content/utils";

/** L'élément retenu par le sélecteur : de quoi appeler fix-source et afficher un récapitulatif. */
export interface PickedContent {
	id: string;
	title: string;
	contentType: string;
	slug?: string | null;
	path?: string | null;
}

type ContentPickerPanelProps = {
	/** Contenus à masquer (ex. la page retirée elle-même). */
	excludeIds?: Array<string | null | undefined>;
	/** Id actuellement sélectionné (contrôlé par le parent). */
	selectedId?: string | null;
	onSelect: (item: PickedContent) => void;
	/** Hauteur maximale de la liste (classe Tailwind), par défaut max-h-64. */
	listMaxHeightClass?: string;
};

const PAGE_SIZE = 8;

/**
 * Sélecteur de contenu réutilisable : liste TOUS les contenus (GET /api/content/list : le même
 * endpoint que le navigateur de contenus, pagination et recherche côté serveur) avec un filtre
 * par type et une recherche texte. Les contenus supprimés ne figurent pas dans cette liste
 * (l'endpoint ne sert que les contenus actifs) et les types techniques/singletons sont masqués.
 */
const ContentPickerPanel: React.FC<ContentPickerPanelProps> = ({
	excludeIds,
	selectedId,
	onSelect,
	listMaxHeightClass = "max-h-64"
}) => {
	const [items, setItems] = useState<PickedContent[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [contentType, setContentType] = useState("all");
	const [types, setTypes] = useState<BCCADMINBaseModelsContentTypeFilterItem[]>([]);
	const loadSeqRef = useRef(0);

	const excludeSet = useMemo(
		() => new Set((excludeIds ?? []).filter((id): id is string => Boolean(id))),
		[excludeIds]
	);

	// Recherche débouncée : pas une requête par frappe.
	useEffect(() => {
		const handle = window.setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(1);
		}, 300);
		return () => window.clearTimeout(handle);
	}, [searchInput]);

	const load = useCallback(async () => {
		const seq = ++loadSeqRef.current;
		setLoading(true);
		setError(null);
		try {
			const response = await client.content.apiContentListGet(
				page,
				PAGE_SIZE,
				search || undefined,
				undefined,
				contentType === "all" ? undefined : contentType,
				undefined,
				undefined,
				"updated",
				true,
				false
			);
			if (seq !== loadSeqRef.current) return; // réponse périmée : une requête plus récente est partie
			const data = response.data;
			setItems(
				(data?.items ?? [])
					.filter((item) => item.id && !excludeSet.has(item.id))
					.map((item) => ({
						id: item.id ?? "",
						title: item.title?.trim() || item.slug?.trim() || item.id || "Sans titre",
						contentType: item.contentType ?? "",
						slug: item.slug,
						path: item.path
					}))
			);
			setTotalCount(data?.totalCount ?? 0);
			// Le dropdown des types garde tous les types déjà vus pour rester stable une fois filtré.
			const incoming = (data?.contentTypes ?? []).filter(
				(entry) => entry.key && !isHiddenBrowseType(entry.key, entry.title)
			);
			if (incoming.length) {
				setTypes((previous) => {
					const map = new Map<string, BCCADMINBaseModelsContentTypeFilterItem>();
					previous.forEach((entry) => entry.key && map.set(entry.key, entry));
					incoming.forEach((entry) => entry.key && map.set(entry.key, entry));
					return Array.from(map.values()).sort((a, b) =>
						(a.title ?? a.key ?? "").localeCompare(b.title ?? b.key ?? "", "fr", { sensitivity: "base" })
					);
				});
			}
		} catch (err) {
			if (seq !== loadSeqRef.current) return;
			console.error("Échec du chargement des contenus", err);
			setItems([]);
			setTotalCount(0);
			setError("Impossible de charger la liste des contenus.");
		} finally {
			if (seq === loadSeqRef.current) setLoading(false);
		}
	}, [page, search, contentType, excludeSet]);

	useEffect(() => {
		load();
	}, [load]);

	const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
	const canPrev = page > 1;
	const canNext = page < totalPages;

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-[180px] flex-1">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
					<input
						type="search"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						placeholder="Rechercher un contenu…"
						className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
					/>
				</div>
				<select
					value={contentType}
					onChange={(e) => {
						setContentType(e.target.value || "all");
						setPage(1);
					}}
					className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-slate-400 focus:outline-none"
					title="Filtrer par type de contenu"
				>
					<option value="all">Tous les types</option>
					{types.map((entry) => (
						<option key={`picker-type-${entry.key}`} value={entry.key ?? ""}>
							{entry.title ?? entry.key}
						</option>
					))}
				</select>
			</div>

			{error && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
			)}

			<div className={`overflow-y-auto rounded-xl border border-slate-200 bg-white ${listMaxHeightClass}`}>
				{loading ? (
					<div className="space-y-2 p-3">
						{[0, 1, 2, 3].map((row) => (
							<div key={`picker-skel-${row}`} className="h-10 animate-pulse rounded-lg bg-slate-100" />
						))}
					</div>
				) : items.length ? (
					<ul className="divide-y divide-slate-100">
						{items.map((item) => {
							const isSelected = selectedId === item.id;
							return (
								<li key={item.id}>
									<button
										type="button"
										onClick={() => onSelect(item)}
										className={`flex w-full items-center gap-3 px-3 py-2 text-left transition ${
											isSelected ? "bg-sky-50" : "hover:bg-slate-50"
										}`}
									>
										<span
											className={`h-3.5 w-3.5 shrink-0 rounded-full border transition ${
												isSelected ? "border-sky-500 bg-sky-500 ring-2 ring-sky-200" : "border-slate-300 bg-white"
											}`}
											aria-hidden="true"
										/>
										<span className="min-w-0 flex-1">
											<span className="block truncate text-sm font-semibold text-slate-800" title={item.title}>
												{item.title}
											</span>
											{(item.path || item.slug) && (
												<span
													className="block truncate font-mono text-[11px] text-slate-400"
													title={item.path ?? item.slug ?? ""}
												>
													{item.path ?? item.slug}
												</span>
											)}
										</span>
										<span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
											{item.contentType || "-"}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				) : (
					<p className="px-3 py-8 text-center text-sm text-slate-400">
						{search ? `Aucun contenu ne correspond à « ${search} ».` : "Aucun contenu disponible."}
					</p>
				)}
			</div>

			<div className="flex items-center justify-between text-xs text-slate-500">
				<span>
					{totalCount.toLocaleString("fr-FR")} contenu{totalCount > 1 ? "s" : ""}
				</span>
				<div className="flex items-center gap-1">
					<button
						type="button"
						disabled={!canPrev || loading}
						onClick={() => setPage((current) => Math.max(1, current - 1))}
						className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						title="Page précédente"
					>
						<FiChevronLeft />
					</button>
					<span className="px-1">
						{page} / {totalPages}
					</span>
					<button
						type="button"
						disabled={!canNext || loading}
						onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
						className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						title="Page suivante"
					>
						<FiChevronRight />
					</button>
				</div>
			</div>
		</div>
	);
};

export default ContentPickerPanel;
